import { Box, SvgIcon, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import type { StripePlanBenefitsSection } from '../../constants/stripePlanBenefits';
import getMuiIcon from '../../utils/getMuiIcon';

interface StripePlanBenefitsProps {
	benefit: StripePlanBenefitsSection;
}

// Visual twin of `components/PricingPackage/Benefits.tsx`, duplicated rather
// than reused to keep the Stripe upgrade flow independent of the Zuora pricing
// path (avoids regression risk; collapses back to a single component once
// Zuora retires).
const StripePlanBenefits: React.FC<StripePlanBenefitsProps> = ({ benefit }) => {
	const { t } = useTranslation();

	return (
		<Box
			data-testid="stripe-plan-benefit-section"
			sx={{
				display: 'flex',
				flexDirection: 'column',
				gap: spaces.sm,
			}}
		>
			{benefit.title && (
				<Typography variant="h6" color={palette.text.primary} sx={{ fontWeight: 600 }}>
					{t(benefit.title)}
				</Typography>
			)}
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: spaces.xs }}>
				{benefit.features.map((feature, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: index keys are pre-existing; switching the key source changes reconciliation behavior
					<Box key={index} sx={{ display: 'flex', gap: spaces.xs, alignItems: 'center' }}>
						<SvgIcon
							component={getMuiIcon(feature.isHighlighted ? 'checkOutlineVerify' : 'checkVerify')}
						/>
						<Typography
							variant="body2"
							sx={{
								color: feature.isHighlighted ? palette.primary.main : palette.text.secondary,
								fontWeight: feature.isHighlighted ? 500 : 300,
							}}
						>
							{t(feature.text)}
						</Typography>
					</Box>
				))}
			</Box>
		</Box>
	);
};

export default StripePlanBenefits;
